import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
    SITE_NAME,
    TWITTER_HANDLE,
    getArticleJsonLd,
    getOrganizationJsonLd,
    getSeoForPath,
    normalizePath,
} from "../data/seoConfig";
import { useSeoContext } from "../context/SeoContext";

function upsertMeta(attr, key, content) {
    if (content == null || content === "") return;
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function removeMeta(attr, key) {
    const el = document.querySelector(`meta[${attr}="${key}"]`);
    if (el) el.remove();
}

function upsertLink(rel, href, extra = {}) {
    if (!href) return;
    const selector = extra.sizes
        ? `link[rel="${rel}"][sizes="${extra.sizes}"]`
        : `link[rel="${rel}"]`;
    let el = document.querySelector(selector);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (extra.sizes) el.setAttribute("sizes", extra.sizes);
        if (extra.type) el.setAttribute("type", extra.type);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
    let el = document.getElementById(id);
    if (!data) {
        if (el) el.remove();
        return;
    }
    if (!el) {
        el = document.createElement("script");
        el.id = id;
        el.type = "application/ld+json";
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

function setOgImageMeta(image, { width, height, type, alt }) {
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:secure_url", image);
    upsertMeta("property", "og:image:type", type);
    upsertMeta("property", "og:image:width", String(width));
    upsertMeta("property", "og:image:height", String(height));
    upsertMeta("property", "og:image:alt", alt);
    upsertMeta("name", "twitter:image", image);
}

export default function Seo() {
    const { pathname } = useLocation();
    const { pageSeo } = useSeoContext();
    const meta = getSeoForPath(pathname, pageSeo || {});
    const path = normalizePath(pathname);

    useEffect(() => {
        document.title = meta.title;

        upsertMeta("name", "description", meta.description);
        upsertMeta("name", "keywords", meta.keywords);
        upsertMeta("name", "author", SITE_NAME);
        upsertMeta("name", "robots", meta.noindex ? "noindex, nofollow" : "index, follow");

        upsertLink("canonical", meta.canonical);

        upsertMeta("property", "og:title", meta.title);
        upsertMeta("property", "og:description", meta.description);
        upsertMeta("property", "og:url", meta.canonical);
        upsertMeta("property", "og:type", meta.ogType || "website");
        upsertMeta("property", "og:site_name", SITE_NAME);
        upsertMeta("property", "og:locale", "en_IN");
        setOgImageMeta(meta.ogImage, {
            width: meta.ogImageWidth,
            height: meta.ogImageHeight,
            type: meta.ogImageType,
            alt: meta.ogImageAlt,
        });

        upsertMeta("name", "twitter:card", meta.twitterCard || "summary_large_image");
        upsertMeta("name", "twitter:site", TWITTER_HANDLE);
        upsertMeta("name", "twitter:title", meta.title);
        upsertMeta("name", "twitter:description", meta.description);

        if (meta.jsonLd) {
            upsertJsonLd("seo-jsonld", meta.jsonLd);
        } else if (path === "/") {
            upsertJsonLd("seo-jsonld", getOrganizationJsonLd());
        } else if (path.startsWith("/blog/") && pageSeo?.article) {
            upsertJsonLd(
                "seo-jsonld",
                getArticleJsonLd({
                    title: pageSeo.article.title,
                    description: pageSeo.article.description,
                    url: meta.canonical,
                    datePublished: pageSeo.article.datePublished,
                    author: pageSeo.article.author,
                }),
            );
        } else {
            upsertJsonLd("seo-jsonld", null);
        }

        return () => {
            removeMeta("name", "robots");
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- meta fields listed explicitly
    }, [
        meta.title,
        meta.description,
        meta.keywords,
        meta.canonical,
        meta.ogImage,
        meta.ogImageWidth,
        meta.ogImageHeight,
        meta.ogImageType,
        meta.ogImageAlt,
        meta.ogType,
        meta.twitterCard,
        meta.noindex,
        path,
        pageSeo?.article?.title,
        pageSeo?.article?.description,
        pageSeo?.article?.datePublished,
        pageSeo?.article?.author,
    ]);

    return null;
}
